import DataLoadingFailed from "@entrilia/shared/components/DataLoadingFailed";
import InlineLoader from "@entrilia/shared/components/inlineLoader/InlineLoader";
import SearchField from "@entrilia/shared/components/inputs/SearchField";
import { Stack, Typography } from "@mui/material";
import { useState } from "react";
import VerticalNavigationWrapper from "../../common/navigation/VerticalNavigationWrapper";
import ChatGroupSection from "./ChatGroupSection";
import type { ChatGroup } from "./chatGroups";
import { filterChatGroupsByTitle } from "./chatGroups";
import ChatListRow from "./ChatListRow";

interface Props {
  chatGroups: ChatGroup[] | undefined;
  loadingFailed: boolean;
  selectedThreadId?: string;
  onSelect: (threadId: string) => void;
  onDelete: (threadId: string) => void;
}

const ChatList = ({ chatGroups, loadingFailed, selectedThreadId, onSelect, onDelete }: Props) => {
  const [searchText, setSearchText] = useState("");
  const [expandedByUser, setExpandedByUser] = useState<Partial<Record<string, boolean>>>({});

  const foundChatGroups = chatGroups && filterChatGroupsByTitle(chatGroups, searchText);
  const isSearching = !!searchText.trim();

  const isGroupExpanded = (group: ChatGroup) =>
    isSearching ||
    (expandedByUser[group.label] ??
      (group.defaultExpanded || group.chats.some((chat) => chat.threadId === selectedThreadId)));

  const setGroupExpanded = (label: string, expanded: boolean) =>
    setExpandedByUser((current) => ({ ...current, [label]: expanded }));

  const handleSelect = (group: ChatGroup, threadId: string) => {
    setGroupExpanded(group.label, true);
    onSelect(threadId);
  };

  return (
    <VerticalNavigationWrapper width="21rem">
      <Typography variant="h6">Chats</Typography>

      <SearchField fullWidth placeholder="Search chats" onSearch={setSearchText} />

      <Stack spacing={2} sx={{ flex: 1, overflowY: "auto" }}>
        {loadingFailed ? (
          <DataLoadingFailed title="Chats could not be loaded" text="Reload the page to try again." />
        ) : !foundChatGroups ? (
          <InlineLoader />
        ) : foundChatGroups.length === 0 ? (
          <Typography color="textSecondary">No chats found</Typography>
        ) : (
          foundChatGroups.map((group) => (
            <ChatGroupSection
              key={group.label}
              title={group.label}
              expanded={isGroupExpanded(group)}
              toggleDisabled={isSearching}
              onToggle={() => setGroupExpanded(group.label, !isGroupExpanded(group))}
            >
              {group.chats.map((chat) => (
                <ChatListRow
                  key={chat.threadId}
                  chat={chat}
                  selected={chat.threadId === selectedThreadId}
                  onSelect={() => handleSelect(group, chat.threadId)}
                  onDelete={() => onDelete(chat.threadId)}
                />
              ))}
            </ChatGroupSection>
          ))
        )}
      </Stack>
    </VerticalNavigationWrapper>
  );
};

export default ChatList;
